import { useState, useMemo } from 'react';
import { Download, Filter } from 'lucide-react';
import { Toolbar } from '@vnetwork/ui/layout';
import {
  Button,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Badge,
} from '@vnetwork/ui/primitives';
import {
  EmptyState,
  FilterBuilder,
  type FilterCondition,
  type FilterField,
} from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { TabLoading } from '@/pages/customers/detail/tab-states';
import { useContractOverview, exportOverdueContracts, type ContractOverviewItem, type ContractStatus } from '@/api/contracts';
import { ContractOverviewCard } from './contract-overview-card';

/* ─── filter fields ────────────────────────────────────────────────────── */

/** FilterBuilder fields for the kanban overview. */
const overviewFilterFields: FilterField[] = [
  {
    value: 'customer_industry',
    label: 'Industry',
    input: 'text',
  },
  {
    value: 'sla_enable',
    label: 'SLA',
    input: 'select',
    options: [
      { value: 'true', label: 'Enabled' },
      { value: 'false', label: 'Disabled' },
    ],
  },
  {
    value: 'nda_enable',
    label: 'NDA',
    input: 'select',
    options: [
      { value: 'true', label: 'Enabled' },
      { value: 'false', label: 'Disabled' },
    ],
  },
  {
    value: 'employee_id',
    label: 'Employee ID',
    input: 'text',
  },
  {
    value: 'product_group_name',
    label: 'Product group',
    input: 'text',
  },
  {
    value: 'created_at',
    label: 'Created from',
    input: 'text',
  },
];

/* ─── kanban config ────────────────────────────────────────────────────── */

/** Ordered kanban columns — backend spelling preserved (`expiried`). */
const KANBAN_COLUMNS: { status: ContractStatus; label: string }[] = [
  { status: 'pending', label: 'Pending' },
  { status: 'exchanging', label: 'Exchanging' },
  { status: 'uploading', label: 'Uploading' },
  { status: 'active', label: 'Active' },
  // Backend spelling is `expiried` (sic) per phase spec; cast via ContractStatus
  { status: 'expired' as ContractStatus, label: 'Expired' },
];

/** Max cards shown per column before truncation message. */
const MAX_CARDS = 3;

/* ─── helpers ─────────────────────────────────────────────────────────── */

function conditionsToFilters(conditions: FilterCondition[]): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const c of conditions) {
    if (!c.value) continue;
    const values = c.value.split(',').map((s) => s.trim()).filter(Boolean);
    out[c.field] = [...(out[c.field] ?? []), ...values];
  }
  return out;
}

/* ─── component ───────────────────────────────────────────────────────── */

/**
 * Kanban overview tab — contracts grouped by status. Sits inside the `Tabs`
 * wrapper in `ContractsPage`. Toolbar carries a Filters popover + Export
 * Overdue button. Each status column shows up to 3 cards with a count badge.
 * Data from `GET /contracts/customers/overview`.
 */
export function ContractOverviewTab() {
  const [filterOpen, setFilterOpen] = useState(false);
  const [conditions, setConditions] = useState<FilterCondition[]>([]);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const filters = useMemo(() => conditionsToFilters(conditions), [conditions]);
  const appliedCount = new Set(conditions.map((c) => c.field)).size;

  const { data, isLoading, isError, refetch } = useContractOverview(
    Object.keys(filters).length ? { filters } : {},
  );

  const rows: ContractOverviewItem[] = data?.results ?? [];

  /** Group rows by status. */
  const grouped = useMemo(() => {
    const map: Record<string, ContractOverviewItem[]> = {};
    for (const row of rows) {
      const key = row.contract_status ?? 'unknown';
      if (!map[key]) map[key] = [];
      map[key].push(row);
    }
    return map;
  }, [rows]);

  const applyFilters = (next: FilterCondition[]) => {
    setConditions(next.filter((c) => c.value !== ''));
    setFilterOpen(false);
  };

  const handleExportOverdue = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const blob = await exportOverdueContracts();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'overdue-contracts.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setExportError('Failed to export overdue contracts. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)', gridTemplateColumns: 'minmax(0, 1fr)' }}>
      {/* Toolbar */}
      <Toolbar
        style={{ marginBottom: 0 }}
        start={
          <Popover open={filterOpen} onOpenChange={setFilterOpen}>
            <PopoverTrigger asChild>
              <Button variant="secondary" startIcon={<Filter size={15} />}>
                Filters{appliedCount ? <Badge tone="primary">{appliedCount}</Badge> : null}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" style={{ padding: 0 }}>
              <FilterBuilder
                fields={overviewFilterFields}
                defaultValue={conditions.length ? conditions : undefined}
                onApply={applyFilters}
                onClose={() => setFilterOpen(false)}
              />
            </PopoverContent>
          </Popover>
        }
        end={
          <Button
            variant="secondary"
            startIcon={<Download size={15} />}
            loading={exporting}
            onClick={handleExportOverdue}
          >
            Export overdue
          </Button>
        }
      />

      {/* Export error */}
      {exportError ? (
        <Alert tone="danger" title={exportError} />
      ) : null}

      {/* Content */}
      {isError ? (
        <Alert
          tone="danger"
          title="Failed to load overview"
          actions={
            <Button size="sm" variant="ghost" onClick={() => refetch()}>
              Retry
            </Button>
          }
        />
      ) : isLoading ? (
        <TabLoading rows={4} />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No contracts"
          description="No contracts match the current filters."
        />
      ) : (
        /* Kanban board — horizontal scroll on narrow viewports */
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${KANBAN_COLUMNS.length}, minmax(240px, 1fr))`,
            gap: 'var(--vn-space-4)',
            overflowX: 'auto',
            alignItems: 'start',
          }}
        >
          {KANBAN_COLUMNS.map(({ status, label }) => {
            const cards = grouped[status] ?? [];
            const shown = cards.slice(0, MAX_CARDS);
            const overflow = cards.length - shown.length;

            return (
              <div
                key={status}
                style={{
                  display: 'grid',
                  gap: 'var(--vn-space-3)',
                  gridTemplateColumns: 'minmax(0, 1fr)',
                  alignContent: 'start',
                }}
              >
                {/* Column header */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--vn-space-2)',
                    paddingBottom: 'var(--vn-space-2)',
                    borderBottom: '1px solid var(--vn-border-subtle)',
                  }}
                >
                  <span
                    style={{
                      fontSize: 'var(--vn-font-size-sm)',
                      fontWeight: 'var(--vn-font-weight-semibold)',
                    }}
                  >
                    {label}
                  </span>
                  <Badge tone="neutral" size="sm">
                    {cards.length}
                  </Badge>
                </div>

                {/* Cards */}
                {shown.length === 0 ? (
                  <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
                    No contracts
                  </span>
                ) : (
                  shown.map((contract) => (
                    <ContractOverviewCard key={contract.contract_id} contract={contract} />
                  ))
                )}

                {/* Overflow indicator */}
                {overflow > 0 ? (
                  <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
                    +{overflow} more
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
